import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, MapPin, Globe, ShieldCheck, ArrowRight, Loader2, Check, AlertCircle } from 'lucide-react';
import emailjs from '@emailjs/browser';
import PageTransition from '@/components/global/PageTransition';
import SEO from '@/components/global/SEO';
import RasterGridBackdrop from '@/components/ui/RasterGridBackdrop';
import { Content, Language } from '@/types';

interface ContactProps {
  content?: Content;
  lang: Language;
}

const Contact: React.FC<ContactProps> = ({ lang }) => {
  const formRef = useRef<HTMLFormElement>(null);
  
  const [formState, setFormState] = useState({ name: '', email: '', org: '', message: '' });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [isHovered, setIsHovered] = useState(false);

  const isRtl = lang === 'ar';
  const appLoaded = typeof window !== 'undefined' && sessionStorage.getItem('appLoaded') === 'true';

  const title = lang === 'en' ? "Contact Enterprise Uplink | VEXL" : "تواصل مع VEXL";
  const description = lang === 'en'
    ? "Establish a secure uplink with VEXL. Contact our GeoAI engineering team for enterprise location intelligence solutions or academic support."
    : "تواصل مع مهندسي VEXL عبر قناة اتصال آمنة للاستفسار عن حلول الذكاء الاصطناعي الجغرافي للمؤسسات.";

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formRef.current) return;

    setStatus('loading');
    const env = import.meta.env;
    
    emailjs.sendForm(
      env.VITE_EMAILJS_SERVICE_ID,
      env.VITE_EMAILJS_TEMPLATE_ID,
      formRef.current,
      env.VITE_EMAILJS_PUBLIC_KEY
    )
    .then(() => {
        setStatus('success');
        setFormState({ name: '', email: '', org: '', message: '' });
        setTimeout(() => setStatus('idle'), 5000);
    }, () => {
        setStatus('error');
        setTimeout(() => setStatus('idle'), 3000);
    });
  };

  return (
    <PageTransition>
      <SEO 
        title={title}
        description={description}
        lang={lang}
      />
      <div 
        className={`pt-28 pb-24 min-h-screen relative overflow-x-hidden bg-[#05070A] text-[#EDF0F1] ${isRtl ? 'font-arabic text-right' : 'font-sans text-left'}`}
        dir={isRtl ? 'rtl' : 'ltr'}
      >
        <RasterGridBackdrop type="type-a" />

        <div className="max-w-[1400px] mx-auto px-6 relative z-10">
          
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            
            {/* Left Side: Editorial Intel & Location Panel */}
            <div className={`lg:col-span-6 space-y-8 ${isRtl ? 'lg:order-2 text-right' : 'lg:order-1 text-left'}`}>
              <div>
                <div className="inline-flex items-center gap-2 text-[#0E93A3] font-mono text-[11px] tracking-[0.18em] uppercase mb-4 font-semibold">
                  <span className="w-1.5 h-1.5 bg-[#0E93A3] rounded-full animate-pulse" />
                  SECURE ENTERPRISE UPLINK
                </div>
                <h1 
                  className="text-3xl md:text-5xl lg:text-[52px] font-medium text-[#EDF0F1] tracking-[-0.5px] leading-[1.12] mb-6"
                  style={{ fontFamily: "'IBM Plex Sans Condensed', 'Inter', system-ui, sans-serif" }}
                >
                  {lang === 'ar' ? 'تواصل مع مهندسي VEXL' : 'Establish Secure Communication'}
                </h1>
                <p className="text-[#9AA5AC] text-base md:text-[17px] leading-[1.6] max-w-lg font-normal">
                  {lang === 'ar'
                    ? 'سواء كنت بحاجة إلى استشارات معمارية، نشر خوادم معزولة، أو تراخيص مؤسسية للذكاء المكاني، فريقنا مستعد للإجابة.'
                    : 'Whether you require enterprise location intelligence, dedicated on-premise deployments, or academic access, our engineering channels are open.'}
                </p>
              </div>

              {/* Map Coordinates Visualizer */}
              <div className="relative h-56 w-full bg-[#12161A] rounded-xl border border-[#262C31] overflow-hidden group">
                <div className="absolute inset-0 bg-[radial-gradient(rgba(255,255,255,0.03)_1px,transparent_1px)] [background-size:20px_20px] pointer-events-none" />

                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center space-y-2">
                    <div className="w-10 h-10 rounded-full bg-[#171C21] border border-[#262C31] flex items-center justify-center mx-auto text-[#0E93A3] shadow-[0_0_20px_rgba(14,147,163,0.2)]">
                      <Globe size={20} />
                    </div>
                    <div className="text-xs font-mono text-[#EDF0F1] tracking-widest uppercase font-semibold">HQ Node Active</div>
                    <div className="text-[10px] font-mono text-[#66707A]">30.0444° N, 31.2357° E · EPSG:4326</div>
                  </div>
                </div>

                {/* Corner Markers */}
                <div className="absolute top-2 left-2 w-2.5 h-2.5 border-t border-l border-[#0E93A3]/40" />
                <div className="absolute top-2 right-2 w-2.5 h-2.5 border-t border-r border-[#0E93A3]/40" />
                <div className="absolute bottom-2 left-2 w-2.5 h-2.5 border-b border-l border-[#0E93A3]/40" />
                <div className="absolute bottom-2 right-2 w-2.5 h-2.5 border-b border-r border-[#0E93A3]/40" />
              </div>

              {/* Communication Cards */}
              <div className="space-y-3">
                <a
                  href="mailto:vexl.gis@gmail.com"
                  className={`flex items-start gap-4 p-4 rounded-xl bg-[#12161A] border border-[#262C31] hover:border-[#0E93A3]/50 transition-all group ${isRtl ? 'flex-row-reverse text-right' : 'text-left'}`}
                >
                  <div className="p-2.5 bg-[#171C21] rounded-lg text-[#0E93A3] border border-[#262C31] group-hover:border-[#0E93A3]/60 transition-colors">
                    <Mail size={18} />
                  </div>
                  <div>
                    <div className="text-[10px] font-mono text-[#66707A] uppercase tracking-wider mb-0.5">
                      {lang === 'ar' ? 'استفسارات المؤسسات المباشرة' : 'Direct Inquiries'}
                    </div>
                    <div className="text-sm font-mono text-[#EDF0F1] font-semibold group-hover:text-[#0E93A3] transition-colors">
                      vexl.gis@gmail.com
                    </div>
                  </div>
                </a>
                
                <div className={`flex items-start gap-4 p-4 rounded-xl bg-[#12161A] border border-[#262C31] hover:border-[#0E93A3]/50 transition-all ${isRtl ? 'flex-row-reverse text-right' : 'text-left'}`}>
                  <div className="p-2.5 bg-[#171C21] rounded-lg text-[#0E93A3] border border-[#262C31]">
                    <MapPin size={18} />
                  </div>
                  <div>
                    <div className="text-[10px] font-mono text-[#66707A] uppercase tracking-wider mb-0.5">
                      {lang === 'ar' ? 'المقر الرئيسي للعمليات' : 'Base of Operations'}
                    </div>
                    <div className="text-sm font-mono text-[#EDF0F1] font-semibold">
                      {lang === 'ar' ? 'القرية الذكية، محافظة الجيزة، جمهورية مصر العربية' : 'Smart Village, Giza Governorate, Egypt'}
                    </div>
                  </div>
                </div>
              </div>

            </div>

            {/* Right Side: The Uplink Form */}
            <div className={`lg:col-span-6 ${isRtl ? 'lg:order-1' : 'lg:order-2'}`}>
              <motion.div 
                initial={appLoaded ? false : { opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-[#12161A] p-8 md:p-10 rounded-xl border border-[#262C31] relative overflow-hidden"
              >
                <div className="absolute top-0 left-0 w-full h-0.5 bg-[#0E93A3]/50" />
                
                <AnimatePresence mode="wait">
                  {status === 'success' ? (
                    <motion.div
                      key="success"
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0 }}
                      className="flex flex-col items-center justify-center text-center space-y-5 py-12"
                    >
                      <div className="w-16 h-16 bg-[#639922]/10 rounded-full flex items-center justify-center border border-[#639922]/30 text-[#639922]">
                        <Check size={32} />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-[#EDF0F1] mb-2">Transmission Acknowledged</h3>
                        <p className="text-[#9AA5AC] text-sm">Our engineering team will review your parameters and establish contact.</p>
                      </div>
                    </motion.div>
                  ) : status === 'error' ? (
                    <motion.div
                      key="error"
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0 }}
                      className="flex flex-col items-center justify-center text-center space-y-5 py-12"
                    >
                      <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center border border-red-500/30 text-red-500">
                        <AlertCircle size={32} />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-[#EDF0F1] mb-2">Transmission Incomplete</h3>
                        <p className="text-[#9AA5AC] text-sm">Uplink timeout. Please re-verify network connectivity and retry.</p>
                      </div>
                    </motion.div>
                  ) : (
                    <motion.form 
                      ref={formRef}
                      key="form"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="space-y-6" 
                      onSubmit={handleSubmit}
                    >
                      <div className="border-b border-[#262C31] pb-4 mb-6">
                        <h3 className={`text-lg font-semibold text-[#EDF0F1] ${isRtl ? 'text-right' : 'text-left'}`}>
                          {lang === 'en' ? 'Transmit Enterprise Request' : 'إرسال طلب استشارة'}
                        </h3>
                        <span className="text-xs font-mono text-[#66707A] block mt-1">
                          Direct payload dispatch to VEXL Core Operations
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-1.5">
                          <label htmlFor="name" className="text-xs font-mono text-[#9AA5AC] block">
                            {lang === 'en' ? 'Full Name / Representative' : 'الاسم الكامل'}
                          </label>
                          <input 
                            type="text" 
                            name="user_name"
                            required
                            disabled={status === 'loading'}
                            value={formState.name}
                            onChange={(e) => setFormState({...formState, name: e.target.value})}
                            className="w-full bg-[#171C21] border border-[#262C31] rounded-lg px-4 py-3 text-sm text-[#EDF0F1] placeholder-[#66707A] focus:outline-none focus:border-[#0E93A3] transition-all disabled:opacity-50"
                            placeholder="John Doe"
                            id="name"
                          />
                        </div>

                        <div className="space-y-1.5">
                          <label htmlFor="email" className="text-xs font-mono text-[#9AA5AC] block">
                            {lang === 'en' ? 'Enterprise Email' : 'البريد المؤسسي'}
                          </label>
                          <input 
                            type="email" 
                            name="user_email" 
                            required
                            disabled={status === 'loading'}
                            value={formState.email}
                            onChange={(e) => setFormState({...formState, email: e.target.value})}
                            className="w-full bg-[#171C21] border border-[#262C31] rounded-lg px-4 py-3 text-sm text-[#EDF0F1] placeholder-[#66707A] focus:outline-none focus:border-[#0E93A3] transition-all disabled:opacity-50"
                            placeholder="user@enterprise.com"
                            id="email"
                          />
                        </div>
                      </div>

                      <div className="space-y-1.5">
                        <label htmlFor="org" className="text-xs font-mono text-[#9AA5AC] block">
                          {lang === 'en' ? 'Affiliation / Organization' : 'المؤسسة أو الجهة'}
                        </label>
                        <input 
                          type="text" 
                          name="organization"
                          disabled={status === 'loading'}
                          value={formState.org}
                          onChange={(e) => setFormState({...formState, org: e.target.value})}
                          className="w-full bg-[#171C21] border border-[#262C31] rounded-lg px-4 py-3 text-sm text-[#EDF0F1] placeholder-[#66707A] focus:outline-none focus:border-[#0E93A3] transition-all disabled:opacity-50"
                          placeholder="Ministry, Enterprise, or Institute"
                          id="org"
                        />
                      </div>

                      <div className="space-y-1.5">
                        <label htmlFor="message" className="text-xs font-mono text-[#9AA5AC] block">
                          {lang === 'en' ? 'Project Requirements / Technical Scope' : 'تفاصيل المشروع والاحتياجات'}
                        </label>
                        <textarea 
                          rows={4}
                          name="message"
                          required
                          disabled={status === 'loading'}
                          value={formState.message}
                          onChange={(e) => setFormState({...formState, message: e.target.value})}
                          className="w-full bg-[#171C21] border border-[#262C31] rounded-lg px-4 py-3 text-sm text-[#EDF0F1] placeholder-[#66707A] focus:outline-none focus:border-[#0E93A3] transition-all resize-none disabled:opacity-50"
                          placeholder="Specify spatial dataset size, expected deployment model, or research questions..."
                          id="message"
                        />
                      </div>

                      <button 
                        type="submit"
                        disabled={status === 'loading'}
                        onMouseEnter={() => setIsHovered(true)}
                        onMouseLeave={() => setIsHovered(false)}
                        className="w-full py-3.5 bg-[#0E93A3] text-white font-mono text-xs tracking-widest uppercase rounded-full hover:bg-[#075C68] transition-all duration-200 shadow-[0_0_15px_rgba(14,147,163,0.3)] flex items-center justify-center gap-2 font-medium disabled:opacity-70 disabled:cursor-not-allowed"
                      >
                        {status === 'loading' ? (
                          <>
                            <Loader2 className="animate-spin" size={16} />
                            <span>TRANSMITTING...</span>
                          </>
                        ) : (
                          <>
                            <span>{lang === 'en' ? 'Initialize Connection' : 'إرسال الرسالة'}</span>
                            <ArrowRight className={`transition-transform duration-200 ${isHovered ? 'translate-x-1' : ''} ${isRtl && isHovered ? '-translate-x-1' : ''}`} size={16} />
                          </>
                        )}
                      </button>
                    </motion.form>
                  )}
                </AnimatePresence>

                <div className="flex items-center justify-center gap-2 text-[10px] text-[#66707A] font-mono mt-6 pt-4 border-t border-[#262C31]">
                  <ShieldCheck size={12} className="text-[#0E93A3]" />
                  <span>TLS ENCRYPTION ACTIVE // SECURE ENTERPRISE CHANNEL</span>
                </div>

              </motion.div>
            </div>

          </div>
        </div>
      </div>
    </PageTransition>
  );
};

export default Contact;
